import BusyIndicator from "@components/busyIndicator";
import { NoData } from "@components/table/NoData";
import { ITabData, ITabsProps } from "@components/tabs";
import { createBindingContext } from "@odata/BindingContext";
import { EntitySetName, EntityTypeName, IPrYearOperationEntity } from "@odata/GeneratedEntityTypes";
import {
    BackgroundJobStatusCode,
    BackgroundJobTypeCode,
    PrPayrollPeriodStatusCode,
    WebSocketMessageTypeCode
} from "@odata/GeneratedEnums";
import { withOData } from "@odata/withOData";
import Page, { IPageParams, IPageState, IProps as PageIProps } from "@pages/Page";
import { fetchPayrollPeriods } from "@pages/payroll/payroll.utils";
import {
    CREATE_YEAR_OPERATION_URL,
    getPayrollYearOperations,
    SortedPRYearOperationSections
} from "@pages/payroll/payrollYearOperations/PayrollYearOperation.utils";
import PayrollYearOperationView from "@pages/payroll/payrollYearOperations/PayrollYearOperationView";
import customFetch, { getDefaultPostParams } from "@utils/customFetch";
import { logger } from "@utils/log";
import { memoizeOne } from "@utils/memoizeOne";
import { TWebsocketMessage } from "@utils/websocketManager/Websocket.types";
import { isBackgroundJobWebsocketMessage } from "@utils/websocketManager/Websocket.utils";
import WebsocketManager from "@utils/websocketManager/WebsocketManager";
import React from "react";
import { withTranslation } from "react-i18next";
import { RouteComponentProps, withRouter } from "react-router-dom";

import { AppContext, IAppContext } from "../../../contexts/appContext/AppContext.types";
import {
    getBackgroundJobFromWebsocketMessage,
    isRelevantBackgroundJob
} from "../../../contexts/backgroundJobsContext/BackgroundJobsContext.utils";
import { Sort } from "../../../enums";
import { getCorrectPath } from "../../../routes";
import { getUtcDayjs } from "../../../types/Date";
import { FormStorage } from "../../../views/formView/FormStorage";
import { getDefinitions } from "./PayrollYearOperation.def";

interface IPayrollYearOperationParams extends IPageParams {
    year?: string;
    task?: string;
}

interface IState extends IPageState {
    tabDefinition?: ITabsProps;
    selectedYearDoesNotExist?: boolean;
    hasProcessedPeriods?: boolean;
}

// handles only url and storage initialization
class PayrollYearOperation extends Page<PageIProps<IPayrollYearOperationParams>, IState> {
    static contextType = AppContext;
    static defaultProps = {
        getDef: getDefinitions
    };

    storage: FormStorage;
    fileViewRef = React.createRef<any>();

    state: IState = {
        tabDefinition: undefined,
        selectedYearDoesNotExist: false,
        hasProcessedPeriods: false
    }

    _unsubscribeWebsocket: () => void;

    constructor(props: PageIProps, context: IAppContext) {
        super(props, context);
        this.storage = new FormStorage({
            id: EntityTypeName.PrYearOperation,
            definition: getDefinitions(context).form,
            oData: props.oData,
            t: props.t,
            context,
            initialHistoryState: props.history.location.state,
            refresh: () => {
                this.fileViewRef.current?.forceUpdate();
                this.forceUpdate();
            }
        });
    }

    async componentDidMount() {
        super.componentDidMount();

        this._unsubscribeWebsocket = WebsocketManager.subscribe({
            callback: this.handleWebsocketMessage,
            types: [WebSocketMessageTypeCode.BackgroundJob]
        });

        if (!this.props.match.params.year) {
            await this.correctUrl();
            return;
        }

        await this.init();
    }

    async componentDidUpdate(prevProps: PageIProps<IPayrollYearOperationParams>) {
        super.componentDidUpdate(prevProps);
        if (this.props.match.params.year !== prevProps.match.params.year) {
            await this.init();
        }
    }

    handleWebsocketMessage = (message: TWebsocketMessage): void => {
        if (!isBackgroundJobWebsocketMessage(message)) {
            return;
        }

        const backgroundJob = getBackgroundJobFromWebsocketMessage(message, this.storage.context.getData().companies);
        const reloadTableJobTypes = [
            BackgroundJobTypeCode.GeneratePayrollSheets
        ];

        if (isRelevantBackgroundJob({
            backgroundJob,
            context: this.storage.context,
            relevantJobTypes: reloadTableJobTypes,
            relevantJobStatuses: [
                BackgroundJobStatusCode.Finished
            ]
        })) {
            this.storage.reload();
        }
    };

    getAllEntities = memoizeOne(async (): Promise<IPrYearOperationEntity[]> => {
        return getPayrollYearOperations(this.props.oData);
    });

    async init() {
        const entities = await this.getAllEntities();
        const selectedYear = entities.find(pyo => pyo.Year?.toString() === this.props.match.params.year);
        if (!selectedYear) {
            // todo: how to handle this case?
            this.setState({ selectedYearDoesNotExist: true });
            return;
        }

        const processedPeriods = await fetchPayrollPeriods(this.storage.oData, {
            status: [PrPayrollPeriodStatusCode.ProcessedAndClosed],
            year: selectedYear.Year,
            limit: 1,
            dir: Sort.Asc
        });

        this.setState({ hasProcessedPeriods: processedPeriods.length > 0 });


        const bindingContext = createBindingContext(EntitySetName.PrYearOperations, this.props.oData.metadata).addKey(selectedYear);
        await this.storage.init({
            definition: getDefinitions(this.context).form,
            bindingContext
        });

        this.getTabsDefinition().then(tabDefinition => {
            this.setState({ tabDefinition });
        });

        this.storage.refresh();
    }

    handleTaskSelect = (taskId: string) => {
        const path = getCorrectPath(this.props.match.path, {
            year: this.props.match.params?.year,
            task: taskId
        });

        this.props.history.push(path);
    }

    handleYearSelect = (year: string) => {
        const path = getCorrectPath(this.props.match.path, {
            year: year,
            task: this.props.match.params?.task ?? SortedPRYearOperationSections[0]
        });

        this.props.history.push(path);
    }

    correctUrl = async () => {
        // get previous year operation and redirect to it
        const entities = await this.getAllEntities();
        const today = getUtcDayjs();
        let yearToSelect: number | null = today.year() - 1;

        const isAfterJanuaryPayDay = today.month() > 0 || (today.month() === 0 && today.date() >= 20); // TODO: Replace 20 with actual payday from company settings, currently not available
        const hasLastYearPyo = entities.some(pyo => pyo.Year === today.year() - 1);

        if (!hasLastYearPyo && isAfterJanuaryPayDay) {
            // Payroll year operation for previous year is usually created by BE automatically by processing december payroll period
            // but there is a case, when agenda does not have any employee in the december period and year operation is not created
            // so we need to handle this case as well, and we try to create year operation manually for last year by calling rest endpoint
            try {
                this.getAllEntities.reset();
                const response = await customFetch(CREATE_YEAR_OPERATION_URL.replace("{year}", (today.year() - 1).toString()), getDefaultPostParams());
                // TODO just log the specific errors, or give users some feedback?
                // 400 when a year operation for that year already exists
                // 404 when there is no payroll period for that year
                // 204 when the year operation is created
                if (!response.ok) {
                    yearToSelect = entities[entities.length - 1]?.Year ?? today.year();
                }
            } catch (e) {
                logger.error("Error while creating PrYearOperation for last year", e);
            }
        } else if (!hasLastYearPyo && !isAfterJanuaryPayDay) {
            yearToSelect = entities[entities.length - 1]?.Year ?? today.year();
        }

        this.handleYearSelect(yearToSelect.toString());
    }

    async getTabsDefinition(): Promise<ITabsProps> {
        const entities = await this.getAllEntities();

        function createTabFromEntity(pyo: IPrYearOperationEntity): ITabData {
            return {
                id: pyo.Year.toString(),
                title: pyo.Year.toString()
            };
        }

        const data = entities?.filter(pyo => !pyo.IsClosed).map(createTabFromEntity) ?? [];
        const additionalData = entities?.filter(pyo => pyo.IsClosed).map(createTabFromEntity) ?? [];

        if (data.length === 0 && additionalData.length > 0) {
            // at least one tab should be in main data even if all year operations are closed
            data.push(additionalData.shift() as ITabData);
        }

        return {
            data,
            additionalData,
            selectedTabId: this.props.match.params?.year,
            onChange: this.handleYearSelect
        };
    }

    render() {
        if (this.state.selectedYearDoesNotExist) {
            return <NoData/>;
        }

        if (!this.isReady() || !this.props.match.params?.year || !this.storage.loaded) {
            return <BusyIndicator/>;
        }

        return (
                <PayrollYearOperationView
                        year={this.props.match.params?.year}
                        task={this.props.match.params?.task}
                        storage={this.storage}
                        hasProcessedPeriods={this.state.hasProcessedPeriods}
                        tabsDefinition={this.state.tabDefinition}
                        fileViewRef={this.fileViewRef}
                        onTaskSelect={this.handleTaskSelect}
                />
        );
    }
}

export default withRouter<RouteComponentProps<IPayrollYearOperationParams>, any>(withTranslation(["PayrollYearOperation"])(withOData(PayrollYearOperation)));